import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetShows, apiGetAdvancingProgress, type ShowDTO, type AdvancingProgress } from '../api';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

function fmtDate(d?: string): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

interface Row {
  show: ShowDTO;
  progress: AdvancingProgress;
}

function statusBadge(status: 'complete' | 'incomplete', labelComplete: string, labelIncomplete: string) {
  const complete = status === 'complete';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: '0.72rem',
        fontWeight: 600,
        // LOT U1b — jetons + encre mesurée (le `#fff` sur `#2e7d32` tombait à
        // 3.3:1 alors que le seuil texte est 4.5).
        color: complete ? 'var(--on-success)' : 'var(--on-danger)',
        background: complete ? 'var(--success)' : 'var(--danger)',
      }}
    >
      {complete ? labelComplete : labelIncomplete}
    </span>
  );
}

function progressCell(p: { total: number; confirmed: number; status: 'complete' | 'incomplete' }, fmt: (pct: number) => string) {
  const pct = p.total > 0 ? Math.round((p.confirmed / p.total) * 100) : 0;
  return (
    <div title={`${p.confirmed}/${p.total}`} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <div style={{ flex: 1, minWidth: 50, background: 'var(--border)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: p.status === 'complete' ? 'var(--success)' : 'var(--accent)' }} />
      </div>
      <span style={{ fontSize: '0.72rem', minWidth: 34, textAlign: 'right', color: 'var(--text-dim)' }}>
        {fmt(pct)}
      </span>
    </div>
  );
}

/**
 * Advancing Hub — global per-band view of every show's promoter-advancing
 * progress (2026-09-04). Aggregates GET /shows/:id/advancing/progress across
 * all the band's shows into one table (tech / hotel / logistics / global), with
 * status badges and a link to open each show's own Advancing tab. This is the
 * Phase-E manager dashboard that was missing.
 */
export default function AdvancingHubPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profiles, activeProfileId } = useProfileContext();
  const activeBand = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Masquer les dates passées par défaut (même convention que Shows et Itinerary).
  const [hidePast, setHidePast] = useState(true);

  const load = useCallback(() => {
    if (!activeProfileId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    apiGetShows({ band: activeProfileId })
      .then(async (shows) => {
        const withProgress = await Promise.all(
          shows.map(async (show) => {
            try {
              const progress = await apiGetAdvancingProgress(show.id);
              return { show, progress };
            } catch {
              return null;
            }
          }),
        );
        setRows(withProgress.filter((r): r is Row => r !== null));
      })
      .catch((e: any) => setError(e?.message || 'Failed to load advancing hub'))
      .finally(() => setLoading(false));
  }, [activeProfileId]);

  useEffect(() => { void load(); }, [load]);

  const fmtPct = (pct: number) => `${pct}%`;
  const sorted = [...rows].sort((a, b) => String(a.show.date || '').localeCompare(String(b.show.date || '')));

  // Filtre d'affichage uniquement — ne touche jamais aux données ni à la progression.
  const visible = useMemo(() => {
    if (!hidePast) return sorted;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return sorted.filter(({ show }) => {
      // Un show sans date reste affiché (on ne masque que ce qui est daté).
      if (!show.date) return true;
      return new Date(show.date).getTime() >= today.getTime();
    });
  }, [sorted, hidePast]);

  return (
    <div ref={denseRef} className="page">
      <PageHeader
        title={withPageIcon('advancingHub.title', t('advancingHub.title'))}
        actions={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
            <input
              type="checkbox"
              checked={hidePast}
              onChange={(e) => setHidePast(e.target.checked)}
            />
            {t('shows.hidePast')}
          </label>
        }
      />

      {activeBand && <p className="text-dim" style={{ marginTop: 0 }}>{t('advancingHub.forBand', { band: activeBand.name })}</p>}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('advancingHub.loading')}</p>}

      {!loading && visible.length === 0 && (
        <p className="text-dim">{t('advancingHub.empty')}</p>
      )}

      {!loading && visible.length > 0 && (
        <div className="table-wrapper" style={{ maxWidth: 1100 }}>
          <table className="tbl-dense">
            <thead>
              <tr>
                <th>{t('advancingHub.colDate')}</th>
                <th>{t('advancingHub.colShow')}</th>
                <th>{t('advancingHub.colTech')}</th>
                <th>{t('advancingHub.colHotel')}</th>
                <th>{t('advancingHub.colLogistics')}</th>
                <th>{t('advancingHub.colGlobal')}</th>
                <th>{t('advancingHub.colStatus')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ show, progress }) => {
                const s = progress.scopes;
                const g = progress.global;
                return (
                  <tr key={String(show.id)} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tour/shows/${show.slug || show.id}`)}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(show.date)}</td>
                    <td style={{ fontWeight: 600 }}>{show.name || '—'}</td>
                    <td style={{ minWidth: 90 }}>{progressCell(s.tech, fmtPct)}</td>
                    <td style={{ minWidth: 90 }}>{progressCell(s.hotel, fmtPct)}</td>
                    <td style={{ minWidth: 90 }}>{progressCell(s.logistics, fmtPct)}</td>
                    <td style={{ minWidth: 90 }}>{progressCell(g, fmtPct)}</td>
                    <td>{statusBadge(g.status, t('advancingHub.complete'), t('advancingHub.incomplete'))}</td>
                    <td><span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>→</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
